import React, {useState, useRef, useImperativeHandle, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';

import {showHighCV} from 'lib/protocolHelper';
import {useDispatch} from 'react-redux';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import NumericInput from '../../components/Inputs/NumericInput';
import {ColumnLayout} from '../../components/Layout/Layout';

import {useEnterPress} from '../../lib/customHooks';
import {mergeRefs} from '../../lib/refHelper';
import withScrollInput from '../../lib/withScrollInput';

import './SetVolumeModal.scss';
import HighCV from './SetAspirateDispenseModal/HighCV';

const INCREMENT = 0.1;

const BasicVolume = React.forwardRef(
  (
    {min, max, increment, value, onChange, onClose, titleId, minTipCapacity, recommendedTipCapacity, modalType},
    ref,
  ) => {
    const numboxRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
      numboxRef.current.focus();
    }, []);

    return (
      <div ref={ref} className="broadcast-volume">
        <div className="broadcast-volume-title">
          <span>
            <FormattedHTMLMessage id={titleId} />
          </span>
          <div className="close-button" onClick={onClose}>
            x
          </div>
        </div>
        <div className="broadcast-volume-input">
          <NumericInput ref={numboxRef} value={value} onChange={onChange} min={min} max={max} increment={increment} />
          <div className="volume-text">
            <div>μL</div>
            <div>Volume</div>
          </div>
        </div>
        {dispatch(showHighCV(value)) && (
          <HighCV
            customClass="high-cv"
            minCapacity={minTipCapacity}
            recommendedCapacity={recommendedTipCapacity}
            taskType={modalType}
          />
        )}
      </div>
    );
  },
);

BasicVolume.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  increment: PropTypes.number,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  titleId: PropTypes.string,
  minTipCapacity: PropTypes.number,
  recommendedTipCapacity: PropTypes.number,
  modalType: PropTypes.string,
};

BasicVolume.defaultProps = {
  min: 0,
  max: 9999,
  increment: 0.1,
  value: 0,
  onChange: () => {},
  onClose: () => {},
  titleId: '',
  minTipCapacity: 0.1,
  recommendedTipCapacity: 0.1,
  modalType: '',
};

const ScrollableBroadcastVolume = withScrollInput(BasicVolume);

const SetVolumeModal = React.forwardRef(
  (
    {
      top,
      left,
      intl,
      allowOutsideClick,
      allowPressEnter,
      notifyVolumeChange,
      notifyModalClose,
      maxValue,
      minTipCapacity,
      recommendedTipCapacity,
      modalType,
    },
    ref,
  ) => {
    const [isShow, setIsShow] = useState(false);
    const [volume, setVolume] = useState(0);

    const promiseRef = useRef({});
    const containerRef = useRef(null);

    const titleId = `protocolEditor.${modalType.toLowerCase()}.volumeModal.title`;

    const apply = useCallback(() => {
      if (!isShow) {
        return;
      }

      if (!volume || volume === 0) {
        return;
      }

      const {resolve} = promiseRef.current;

      resolve({volume});
      setIsShow(false);
    }, [isShow, volume]);

    function onClose(needNotify = false) {
      if (needNotify) {
        notifyModalClose();
      }

      apply();

      if (!volume || volume === 0) {
        const {resolve} = promiseRef.current;
        if (resolve) {
          resolve({volume});
        }
        setIsShow(false);
      }
    }

    function onEnterPress() {
      if (!allowPressEnter) {
        return;
      }

      apply();
    }

    useEffect(() => {
      function onClickOutside(event) {
        if (containerRef?.current && !containerRef.current.contains(event.target)) {
          if (!allowOutsideClick) {
            return;
          }

          apply();
        }
      }

      document.addEventListener('mousedown', onClickOutside);
      // return function to be called when unmounted
      return () => {
        document.removeEventListener('mousedown', onClickOutside);
      };
    }, [allowOutsideClick, apply]);

    useEnterPress(containerRef, onEnterPress);

    function handleVolumeChange(val) {
      if (typeof val === 'string' && (val === '' || val[val.length - 1] === '.')) {
        setVolume(val);

        return;
      }

      setVolume(val);
      notifyVolumeChange(val);
    }

    useImperativeHandle(ref, () => ({
      show(initialVolume = 0) {
        return new Promise((resolve, reject) => {
          promiseRef.current = {resolve, reject};

          setVolume(initialVolume);
          setIsShow(true);
        });
      },
      hide() {
        return new Promise((resolve, reject) => {
          promiseRef.current = {resolve, reject};

          setIsShow(false);
        });
      },
      closeAndApply() {
        onClose();
      },
    }));

    return (
      <ColumnLayout
        ref={mergeRefs(ref, containerRef)}
        position="absolute"
        borderRadius="4px"
        top={`${top}px`}
        left={`${left}px`}
        overflow="hidden"
        height="fit-content"
        maxWidth="fit-content"
      >
        {isShow && (
          <div style={{zIndex: 999}}>
            <ScrollableBroadcastVolume
              min={0}
              max={maxValue}
              increment={INCREMENT}
              value={volume}
              onChange={handleVolumeChange}
              onClose={() => onClose(true)}
              intl={intl}
              titleId={titleId}
              minTipCapacity={minTipCapacity}
              recommendedTipCapacity={recommendedTipCapacity}
              modalType={modalType}
            />
          </div>
        )}
      </ColumnLayout>
    );
  },
);

SetVolumeModal.propTypes = {
  top: PropTypes.number,
  left: PropTypes.number,
  intl: PropTypes.any.isRequired,
  allowOutsideClick: PropTypes.bool,
  allowPressEnter: PropTypes.bool,
  notifyVolumeChange: PropTypes.func,
  notifyModalClose: PropTypes.func,
  maxValue: PropTypes.number,
  minTipCapacity: PropTypes.number,
  recommendedTipCapacity: PropTypes.number,
  modalType: PropTypes.string,
};

SetVolumeModal.defaultProps = {
  top: 0,
  left: 0,
  allowOutsideClick: false,
  allowPressEnter: false,
  notifyVolumeChange: () => {},
  notifyModalClose: () => {},
  maxValue: 9999,
  minTipCapacity: 0.1,
  recommendedTipCapacity: 0.1,
  modalType: '',
};

function areEqual(prevProps, nextProps) {
  return (
    prevProps.top === nextProps.top &&
    prevProps.left === nextProps.left &&
    prevProps.volume === nextProps.volume &&
    prevProps.allowOutsideClick === nextProps.allowOutsideClick &&
    prevProps.excludedComponentRef === nextProps.excludedComponentRef
  );
}

export default React.memo(SetVolumeModal, areEqual);
